/**
 * creates a named-date-range button for the date range selector
 */
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns';
import React, { FCC } from 'react';
import Button from '../Button/Button';
import { IRangeDefinition, IRangeDefinitionButtonProps } from './types';

// returns a RangeDefinition object representing today
function today() {
  return {
    end: endOfDay(new Date()),
    label: `Today`,
    start: startOfDay(new Date()),
  };
}

function recent(n: number) {
  const days = {
    [-1]: 'Yesterday',
    [0]: 'Today',
    [1]: 'Tomorrow',
  };

  return {
    end: addDays(today().end, n),
    label: days[n],
    start: addDays(today().start, n),
  };
}

/**
 * behaves like an identity function if the range param is a RangeDefinition object
 * if range param is a number returns a RangeDefinition object
 * @param range
 */
function createRangeDefinition(range: IRangeDefinition | number): IRangeDefinition {
  if (typeof range === 'object') {
    return range;
  } else if (range >= -1 && range <= 1) {
    return recent(range);
  } else {
    // range is a non-zero integer representing the number of days before (negative) or after (positive) today
    const T = today;
    const delta = Math.abs(range);
    const start = range > 0 ? addDays(T().start, 1) : subDays(T().start, delta);
    const end = range > 0 ? addDays(T().end, delta) : subDays(T().end, 1);
    const prefix = range > 0 ? 'Next' : 'Last';

    return {
      end,
      label: `${prefix} ${delta} days`,
      start,
    } as IRangeDefinition;
  }
}

export const RangeDefinitionButton: FCC<IRangeDefinitionButtonProps> = (props) => {
  const def = createRangeDefinition(props.range);
  return <Button label={def.label} onClick={() => props.onClick(def)} />;
};

export default RangeDefinitionButton;
